// 画面側。最初の表示はサーバーが作ったものを使い、そのあとの操作とページの移動はここで描き直す
import type { Page, TopicView, Post, SearchResult, EditLogEntry, ArenaView, CaseView, DebateView } from '../shared/types';
import { waitAskBox,
  header, sideNav, renderPage, freshUi, listBody, searchBody, firstStanceChips, similarBox, replyBox, factBox, tagsBox,
  topicEditBox, editLogBox, proposalBox, confirmBox, composer, chainBox, addendumBox, shareBox, reportBox,
  debateOpenBox, draftHistoryBox, debateWaitBox, atodashiBox, topicMenuBox, topicReportBox, siteShareBox, type Ui,
} from '../shared/render';
import { NONE, VIEW_MS, PRESET_TAGS, parseTags, sortTags, cut, esc, ago } from '../shared/util';
import { skinFontUrl } from '../shared/trophy';

declare global {
  interface Window { turnstile?: { render(el: HTMLElement, o: Record<string, unknown>): string; getResponse(id: string): string | undefined; reset(id: string): void } }
}

let page: Page = JSON.parse(document.getElementById('init')!.textContent || '{}');
// 開いたときの版。サイトが更新されたあとに次のページへ移ったら、ページごと読み込み直して新しい画面にする
const BUILD = page.ver;
let ui: Ui = freshUi();
const $app = document.getElementById('app')!;
const $modal = document.getElementById('modal')!;
const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

// ---------- サーバーとのやり取り ----------
async function api<T = { ok: true; topic?: TopicView }>(path: string, body?: unknown): Promise<T> {
  const r = await fetch(path, body === undefined ? {} : {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  // 仮のアカウントが作られたら、ログインしている状態として画面を描き直す（上の帯・左のメニュー）
  const guest = r.headers.get('X-Gironba-Guest');
  if (guest && !page.viewer.loggedIn) {
    page.viewer.loggedIn = true; page.viewer.isGuest = true; page.viewer.handle = decodeURIComponent(guest);
    const h = document.querySelector('header.site'); if (h) h.outerHTML = header(page.viewer);
    const sn = document.querySelector('.side-nav'); if (sn) sn.outerHTML = sideNav(page.viewer);
    markNav();
  }
  const j = await r.json().catch(() => ({ error: "エラーが発生しました" })) as { error?: string };
  if (!r.ok || j.error) throw new Error(j.error || 'エラーが発生しました');
  return j as T;
}
// 失敗したら理由を出す。成功したら true
async function attempt(fn: () => Promise<void>): Promise<boolean> {
  try { await fn(); return true; } catch (e) { toast((e as Error).message); return false; }
}

// ---------- ページの移動 ----------
async function navigate(url: string, push = true) {
  const u = new URL(url, location.href);
  try {
    const r = await fetch(u.pathname + u.search, { headers: { 'X-Gironba-Data': '1' } });
    if (r.redirected) { location.href = r.url; return; }   // ログインが必要なページなど
    const next = await r.json() as Page;
    if (next.ver && BUILD && next.ver !== BUILD) { location.href = u.href; return; }
    page = next;
  } catch {
    location.href = u.href; return;
  }
  flushViews();
  if (push) history.pushState({ inSite: true }, '', u.pathname + u.search + u.hash);
  ui = freshUi();
  tabInd = null; seenIds = null;
  draw();
  // 「使い方」の項目など、# 付きのアドレスはその場所まで動かす
  const target = u.hash && document.getElementById(decodeURIComponent(u.hash.slice(1)));
  if (target) target.scrollIntoView(); else window.scrollTo(0, 0);
}
window.addEventListener('popstate', () => navigate(location.href, false));

function needLogin(): boolean {
  // 登録しなくても書き込める期間は、ログインを求めない（操作したときにサーバーが仮のアカウントを作る）
  if (page.viewer.loggedIn || page.viewer.guestOK) return false;
  navigate('/login?next=' + encodeURIComponent(location.pathname + location.search));
  return true;
}

// ---------- 描画 ----------
function draw() {
  document.title = page.title;
  const h = document.querySelector('header.site');
  const before = Number(document.getElementById('inbox-count')?.textContent || 0);
  if (h) h.outerHTML = header(page.viewer);
  const sn = document.querySelector('.side-nav');
  if (sn) sn.outerHTML = sideNav(page.viewer);
  if (page.viewer.unread > before) document.getElementById('inbox-count')?.classList.add('pop');
  $app.innerHTML = renderPage(page, ui);
  afterRender();
}
// 話題の画面だけを描き直す（データが変わったとき）
function setTopic(t: TopicView | undefined) {
  if (t && page.data.kind === 'topic') page.data.topic = t;
  $app.innerHTML = renderPage(page, ui);
  afterRender();
}
function topic(): TopicView | null { return page.data.kind === 'topic' ? page.data.topic : null; }
function post(id: string): Post | undefined { return topic()?.posts.find(p => p.id === id); }

// ---------- 動き ----------
// 参考：モーションリファレンス（https://motion-reference.takayustudio.jp/）。使う場所を絞っている
function toast(msg: string): HTMLElement {
  let box = document.getElementById('toasts');
  if (!box) { box = document.createElement('div'); box.id = 'toasts'; document.body.appendChild(box); }
  // 追加前の位置を覚えておき、追加後の位置との差を打ち消してから戻す（FLIP）
  const before = new Map([...box.children].map(el => [el, el.getBoundingClientRect().top]));
  const t = document.createElement('div');
  t.className = 'toast'; t.textContent = msg;
  box.appendChild(t);
  if (!reduceMotion()) {
    for (const [el, top] of before) {
      const dy = top - el.getBoundingClientRect().top;
      el.animate([{ transform: `translateY(${dy}px)` }, { transform: 'none' }], { duration: 360, easing: 'cubic-bezier(.34,1.3,.64,1)' });
    }
  }
  setTimeout(() => { t.classList.add('leaving'); setTimeout(() => t.remove(), 250); }, 2600);
  return t;
}
function shake(el: Element | null) {
  if (!el) return;
  (el as HTMLElement).focus?.();
  el.classList.add('shake'); setTimeout(() => el.classList.remove('shake'), 400);
}

let pop: string | null = null;
let seenIds: Set<string> | null = null;
let tabInd: { x: number; w: number } | null = null;

// 議論場の発言の並べ方（縦に並べる／左右に分ける）。この端末にだけ覚えておく
function readArenaView(): string { try { return localStorage.getItem('arenaView') || 'list'; } catch { return 'list'; } }
document.addEventListener('focusin', (e) => {
  (e.target as Element).closest?.('.compose-inline')?.classList.add('open');
});
// 議論場の「…」は、外を押したら閉じる
document.addEventListener('click', (e) => {
  document.querySelectorAll<HTMLDetailsElement>('details.arena-menu[open]').forEach(m => { if (!m.contains(e.target as Node)) m.open = false; });
});
function markArenaView(view = readArenaView()) {
  document.querySelector('.arena-turns')?.classList.toggle('split', view === 'split');
  document.querySelectorAll<HTMLButtonElement>('[data-arena-view]').forEach(b => b.classList.toggle('on', b.dataset.arenaView === view));
}
document.addEventListener('click', (e) => {
  const b = (e.target as Element).closest<HTMLButtonElement>('[data-arena-view]');
  if (!b) return;
  const view = b.dataset.arenaView!;
  try { localStorage.setItem('arenaView', view); } catch { /* 保存できなくても、いまの画面は切り替える */ }
  markArenaView(view);
});

// 観戦人数を出すかどうか（マイページの設定）。この端末にだけ覚えておく
function readWatchers(): string { try { return localStorage.getItem('watchers') || 'hide'; } catch { return 'hide'; } }
// 成人向けの話題を一覧に出すか（cookie。サーバーが一覧を作るときに見る）
function markAdult() {
  const on = /(?:^|; )adult=show/.test(document.cookie);
  document.querySelectorAll<HTMLButtonElement>('[data-adult-set]').forEach(b => b.classList.toggle('on', (b.dataset.adultSet === 'show') === on));
}
document.addEventListener('click', (e) => {
  const b = (e.target as Element).closest<HTMLButtonElement>('[data-adult-set]');
  if (!b) return;
  document.cookie = b.dataset.adultSet === 'show'
    ? `adult=show; path=/; max-age=${60 * 60 * 24 * 400}; SameSite=Lax; Secure`
    : 'adult=; path=/; max-age=0; SameSite=Lax; Secure';
  markAdult();
});
// 左の案内：いまいるページを太くする
function markNav() {
  const p = location.pathname;
  const at = p === '/' || p.startsWith('/tag/') ? 'home' : p.startsWith('/inbox') ? 'inbox' : p.startsWith('/me') ? 'me' : p.startsWith('/guide') ? 'guide' : '';
  document.querySelectorAll<HTMLElement>('.side-nav [data-nav]').forEach(a => a.classList.toggle('on', a.dataset.nav === at));
}
function markWatchers(v = readWatchers()) {
  document.documentElement.classList.toggle('show-watchers', v === 'show');
  document.querySelectorAll<HTMLButtonElement>('[data-watchers-set]').forEach(b => b.classList.toggle('on', b.dataset.watchersSet === v));
}
document.addEventListener('click', (e) => {
  const b = (e.target as Element).closest<HTMLButtonElement>('[data-watchers-set]');
  if (!b) return;
  try { localStorage.setItem('watchers', b.dataset.watchersSet!); } catch { /* 保存できなくても、いまの画面は切り替える */ }
  markWatchers(b.dataset.watchersSet);
});

function afterRender() {
  markTheme();
  markSkin();
  markWatchers();
  markAdult();
  markNav();
  markArenaView();
  placeTabIndicator();
  markTabOverflow();
  // 「それな」を押したボタン：ポップスケール
  if (pop) { document.querySelector(pop)?.classList.add('pop'); pop = null; }
  // 新しく出てきたひとこと：フェード＋スライド（ページを開いた最初の描画では動かさない）
  const ids = [...document.querySelectorAll<HTMLElement>('.xpost[data-pid], .turn[data-pid]')].map(el => el.dataset.pid!);
  if (seenIds) for (const id of ids) if (!seenIds.has(id)) document.querySelector(`[data-pid="${id}"]`)?.classList.add('enter');
  seenIds = new Set([...(seenIds || []), ...ids]);
  mountTurnstile();
  trackViews();
  bounceIfMyTurn();
  bindPage();
}

// タブインジケーター：下線が前のタブの位置から、選んだタブへ伸び縮みしながら移る
function markTabOverflow() {
  document.querySelectorAll<HTMLElement>('.tabs').forEach(tabs => {
    const update = () => {
      tabs.classList.toggle('more-left', tabs.scrollLeft > 4);
      tabs.classList.toggle('more-right', tabs.scrollLeft + tabs.clientWidth < tabs.scrollWidth - 4);
    };
    const on = tabs.querySelector<HTMLElement>('.tab.on');
    if (on && (on.offsetLeft < tabs.scrollLeft || on.offsetLeft + on.offsetWidth > tabs.scrollLeft + tabs.clientWidth)) {
      tabs.scrollLeft = on.offsetLeft - 24;
    }
    tabs.onscroll = update;
    update();
  });
}
window.addEventListener('resize', () => markTabOverflow());

function placeTabIndicator() {
  const tabs = document.querySelector('.tabs');
  const on = tabs?.querySelector('.tab.on span');
  if (!tabs || !on) return;
  let ind = tabs.querySelector<HTMLElement>('.tab-ind');
  if (!ind) { ind = document.createElement('div'); ind.className = 'tab-ind'; tabs.appendChild(ind); }
  const base = tabs.getBoundingClientRect(), r = on.getBoundingClientRect();
  const to = { x: r.left - base.left + tabs.scrollLeft, w: r.width };
  const from = tabInd;
  ind.style.transition = 'none';
  ind.style.width = `${(from || to).w}px`;
  ind.style.transform = `translateX(${(from || to).x}px)`;
  tabInd = to;
  if (!from || reduceMotion()) return;
  void ind.offsetWidth;
  ind.style.transition = '';
  ind.style.width = `${to.w}px`;
  ind.style.transform = `translateX(${to.x}px)`;
}

window.addEventListener('scroll', () => {
  const head = document.querySelector('.tl-header');
  if (head) head.classList.toggle('compact', window.scrollY > 60 && matchMedia('(max-width: 600px)').matches);
}, { passive: true });

// ---------- ロボット対策（Cloudflare Turnstile） ----------
const widgets = new Map<HTMLElement, string>();
let turnstileLoading: Promise<void> | null = null;
function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!turnstileLoading) {
    turnstileLoading = new Promise(res => {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true; s.onload = () => res();
      document.head.appendChild(s);
    });
  }
  return turnstileLoading;
}
// 確認が済むまでは、送信のボタンを押せないようにする
function setChecked(ok: boolean, msg = '') {
  document.querySelectorAll<HTMLButtonElement>('[data-needs-check]').forEach(b => { b.disabled = !ok; });
  const st = document.querySelector<HTMLElement>('.turnstile-status');
  if (st) { st.textContent = msg; st.hidden = !msg; }
}
function mountTurnstile() {
  const els = [...document.querySelectorAll<HTMLElement>('.turnstile')];
  if (!els.length) return;
  const slow = window.setTimeout(() => setChecked(false, '確認に時間がかかっています。しばらく待っても進まない場合は、ページを再読み込みしてください。'), 8000);
  loadTurnstile().then(() => {
    for (const el of els) {
      if (!el.isConnected || widgets.has(el)) continue;
      const box = document.createElement('div');
      el.prepend(box);
      try {
        widgets.set(el, window.turnstile!.render(box, {
          sitekey: el.dataset.sitekey, language: 'ja',
          callback: () => { clearTimeout(slow); setChecked(true); },
          'expired-callback': () => setChecked(false, '確認の有効期限が切れました。もう一度確認しています…'),
          'error-callback': () => { clearTimeout(slow); setChecked(false, '確認に失敗しました。ページを再読み込みしてください。'); },
        }));
      } catch {
        setChecked(false, '確認を始められませんでした。ページを再読み込みしてください。');
      }
    }
  }).catch(() => setChecked(false, '確認を始められませんでした。通信の状態を確かめて、ページを再読み込みしてください。'));
}
function turnstileToken(): string {
  const el = document.querySelector<HTMLElement>('.turnstile');
  const id = el && widgets.get(el);
  return (id && window.turnstile?.getResponse(id)) || '';
}
function resetTurnstile() {
  const el = document.querySelector<HTMLElement>('.turnstile');
  const id = el && widgets.get(el);
  if (id) { setChecked(false, '確認しています…'); window.turnstile?.reset(id); }
}

// ---------- 「見た」の計測：画面に一定時間映ったひとことを、まとめて送る ----------
let observer: IntersectionObserver | null = null;
const viewQueue = new Set<string>();
let viewTopic = '';
function trackViews() {
  observer?.disconnect();
  const t = topic();
  if (!t || !page.viewer.loggedIn) return;
  viewTopic = t.id;
  const timers = new Map<string, number>();
  observer = new IntersectionObserver(entries => {
    for (const e of entries) {
      const id = (e.target as HTMLElement).dataset.pid!;
      if (e.isIntersecting) {
        if (!timers.has(id)) timers.set(id, window.setTimeout(() => { timers.delete(id); viewQueue.add(id); }, VIEW_MS));
      } else {
        clearTimeout(timers.get(id)); timers.delete(id);
      }
    }
  }, { threshold: 0.6 });
  // 自分のひとことは数えない
  document.querySelectorAll<HTMLElement>('.xpost[data-pid]').forEach(el => { if (!post(el.dataset.pid!)?.mine) observer!.observe(el); });
}
function flushViews() {
  if (!viewQueue.size || !viewTopic) return;
  const body = JSON.stringify({ topicId: viewTopic, ids: [...viewQueue] });
  viewQueue.clear();
  fetch('/api/views', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
}
setInterval(flushViews, 15000);
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flushViews(); });

// ---------- 「？」の説明 ----------
// 使い方のページから、その項目（見出しから次の見出しの手前まで）だけを取り出して小窓に出す
let guideHtml: string | null = null;
async function openHelp(id: string) {
  if (!guideHtml) {
    const r = await fetch('/guide', { headers: { 'X-Gironba-Data': '1' } }).then(x => x.json() as Promise<Page>).catch(() => null);
    guideHtml = r && r.data.kind === 'doc' ? r.data.html : '';
  }
  const doc = new DOMParser().parseFromString(`<div>${guideHtml}</div>`, 'text/html');
  const h = doc.getElementById(id);
  let body = '';
  if (h) {
    for (let n = h.nextElementSibling; n && n.tagName !== 'H2'; n = n.nextElementSibling) body += n.outerHTML;
  }
  const title = h ? (h.childNodes[0]?.textContent ?? '') : '使い方';
  openModal(`
    <div class="box help-box">
      <h3>${esc(title)}</h3>
      <div class="doc">${body || '<p>説明が見つかりませんでした。</p>'}</div>
      <div class="row"><a class="ghost-link" href="/guide${id ? `#${id}` : ''}" data-close>使い方をすべて見る</a><button class="ghost small" data-close>閉じる</button></div>
    </div>`, () => {});
}

// ---------- 小窓 ----------
function openModal(html: string, onClick: (e: MouseEvent) => void, sheet = false) {
  $modal.classList.toggle('sheet', sheet);
  $modal.innerHTML = html;
  $modal.hidden = false;
  $modal.querySelector<HTMLElement>('textarea, input[type="text"]')?.focus();
  $modal.onclick = (e) => {
    if (e.target === $modal || (e.target as Element).closest('[data-close]')) { closeModal(); return; }
    onClick(e);
  };
}
function closeModal() { $modal.hidden = true; $modal.classList.remove('sheet'); $modal.innerHTML = ''; }
function ask(title: string, body: string, ok: string, danger = false): Promise<boolean> {
  return new Promise(res => {
    openModal(confirmBox(title, body, ok, danger), e => {
      if ((e.target as Element).closest('[data-ok]')) { closeModal(); res(true); }
    });
    const prev = $modal.onclick!;
    $modal.onclick = (e) => {
      if (e.target === $modal || (e.target as Element).closest('[data-close]')) { closeModal(); res(false); return; }
      prev.call($modal, e);
    };
  });
}
document.addEventListener('keydown', e => { if (e.key === 'Escape' && !$modal.hidden) closeModal(); });

// ---------- 根拠のリンク ----------
// 「＋ 根拠のリンクを付ける」を押すと欄が開く（描き直さずにその場で開く）
document.addEventListener('click', (e) => {
  // 「× 根拠を付けない」：欄を閉じて、入力も消す
  const close = (e.target as Element).closest<HTMLElement>('[data-source-close]');
  if (close) {
    const field = close.closest('.source-field')!;
    field.querySelectorAll<HTMLInputElement>('.source-input').forEach(i => { i.value = ''; });
    field.querySelector<HTMLElement>('.source-box')!.hidden = true;
    field.querySelector<HTMLElement>('[data-source-toggle]')!.hidden = false;
    return;
  }
  const t = (e.target as Element).closest<HTMLElement>('[data-source-toggle]');
  if (!t) return;
  const box = t.parentElement!.querySelector<HTMLElement>('.source-box')!;
  box.hidden = false; t.hidden = true;
  box.querySelector<HTMLInputElement>('input')?.focus();
});
// 入力された根拠のリンクを集める。形が変なら知らせて null
function readSources(root: ParentNode): string[] | null {
  const inputs = [...root.querySelectorAll<HTMLInputElement>('.source-input')];
  const bad = inputs.find(i => i.value.trim() && !/^https?:\/\/\S+\.\S+/.test(i.value.trim()));
  if (bad) { shake(bad); toast('根拠のリンクは https:// から始まるアドレスを入力してください'); return null; }
  return inputs.map(i => i.value.trim()).filter(Boolean);
}

// ---------- 全体のクリック ----------
document.addEventListener('click', (e) => {
  const a = (e.target as Element).closest('a');
  if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || a.target) return;
  const href = a.getAttribute('href') || '';
  if (!href.startsWith('/') || href.startsWith('/auth/') || href.startsWith('//')) return;
  // サイトそのものを共有（右の欄と一番下のリンク）
  if (a.hasAttribute('data-site-share')) {
    e.preventDefault();
    const url = 'https://gironba.com/';
    openModal(siteShareBox(), async (ev) => {
      const t = ev.target as Element;
      if (t.closest('[data-site-share-x]')) { window.open(`https://x.com/intent/post?${new URLSearchParams({ text: 'GIRONBA', url })}`, '_blank', 'noopener'); closeModal(); return; }
      if (t.closest('[data-site-share-copy]')) { await copyText(url, 'リンクをコピーしました'); closeModal(); }
    });
    return;
  }
  // 「←」はブラウザの「戻る」とは別で、いつも一つ上の階層（リンク先）へ行く
  if (a.classList.contains('help-link')) { e.preventDefault(); openHelp(href.split('#')[1] || ''); return; }
  if (a.hasAttribute('data-login') && href === '/login') {
    e.preventDefault(); needLogin(); return;
  }
  e.preventDefault();
  // タブの切り替えなど、履歴を増やさずに移る
  if (a.hasAttribute('data-replace')) { history.replaceState(history.state, '', href); navigate(href, false); return; }
  if (a.matches('.arena-card, .arena-link') && 'startViewTransition' in document && !reduceMotion() && !document.hidden) {
    a.style.viewTransitionName = 'arena';
    const vt = (document as Document & { startViewTransition(cb: () => Promise<void>): { ready: Promise<void>; finished: Promise<void> } })
      .startViewTransition(() => navigate(href));
    // 動きが途中で止められても（別のタブに切り替えたときなど）、移動自体は済んでいるので何もしない
    vt.ready.catch(() => {}); vt.finished.catch(() => {});
    return;
  }
  navigate(href);
});

// ページごとの操作をつなぐ
function bindPage() {
  const d = page.data;
  $app.onclick = null; $app.oninput = null;
  if (d.kind === 'topic') bindTopic();
  else if (d.kind === 'list') bindList();
  else if (d.kind === 'new') bindNew();
  else if (d.kind === 'login') bindLogin();
  else if (d.kind === 'welcome') bindWelcome();
  else if (d.kind === 'me') bindMe();
  else if (d.kind === 'trophies') bindTrophies();
  else if (d.kind === 'arena') bindArena();
  else if (d.kind === 'case') bindCase();
  else if (d.kind === 'debate') bindDebate();
  else if (d.kind === 'admin') bindAdmin();
  else if (d.kind === 'contact') bindContact();
}

// ---------- トップページ ----------
let searchTimer = 0;
function bindList() {
  const input = document.getElementById('search') as HTMLInputElement | null;
  if (!input) return;
  input.oninput = () => {
    ui.query = input.value;
    clearTimeout(searchTimer);
    const body = document.getElementById('list-body')!;
    if ([...ui.query.trim()].length < 2) {
      if (page.data.kind === 'list') body.innerHTML = listBody(page.data.topics, page.data.tag, page.data.sort);
      tabInd = null; placeTabIndicator();
      return;
    }
    searchTimer = window.setTimeout(async () => {
      const q = ui.query;
      const r = await api<SearchResult>('/api/search?q=' + encodeURIComponent(q)).catch(() => null);
      if (r && q === ui.query) body.innerHTML = searchBody(r);
    }, 250);
  };
}

// ---------- 話題の画面 ----------
// 印の付いた話題で「表示する」を押したら、このタブを閉じるまでは伏せない
const REVEALED = 'gironba-revealed';
function revealedList(): string[] { try { return JSON.parse(sessionStorage.getItem(REVEALED) || '[]'); } catch { return []; } }
function revealTopic(id: string) {   // 話題のページでも議論場でも使う
  ui.revealed.add(id);
  try { sessionStorage.setItem(REVEALED, JSON.stringify([...new Set([...revealedList(), id])])); } catch { /* 覚えられなくても、いまは表示する */ }
  setTopic(undefined);
}
// 並び替え（タブの行の右端）
document.addEventListener('change', (e) => {
  const sel = (e.target as Element).closest<HTMLSelectElement>('[data-order-select]');
  if (!sel || !topic()) return;
  ui.order = sel.value as Ui['order']; seenIds = null; setTopic(undefined);
});
function bindTopic() {
  const tv = topic();
  if (tv?.sensitivity && !ui.revealed.has(tv.id) && revealedList().includes(tv.id)) { revealTopic(tv.id); return; }
  restoreGuestDraft();
  $app.onclick = async (e) => {
    const t = topic()!;
    const target = e.target as Element;
    const b = target.closest('button');
    if (!b) {
      // 右欄の行を押すと、その行の操作が出る（スマホ向け）
      const row = target.closest('.card-row');
      if (row) {
        document.querySelectorAll('.card-row.active').forEach(x => { if (x !== row) x.classList.remove('active'); });
        row.classList.toggle('active');
      }
      return;
    }
    if (b.disabled) return;
    const d = b.dataset;

    // 見るだけの操作
    if (d.filter) { ui.filter = d.filter; seenIds = null; setTopic(undefined); window.scrollTo(0, 0); return; }
    if (d.order) { ui.order = d.order as Ui['order']; seenIds = null; setTopic(undefined); return; }
    if (d.unfold) { ui.openFolded.has(d.unfold) ? ui.openFolded.delete(d.unfold) : ui.openFolded.add(d.unfold); setTopic(undefined); return; }
    if (d.share !== undefined) { openShare(t); return; }   // ログインしていない人も共有できる
    if (d.sensReveal !== undefined) { revealTopic(t.id); return; }
    if (d.topicMenu !== undefined) {
      openModal(topicMenuBox(t, page.viewer.isAdmin), async (ev) => {
        const m = (ev.target as Element).closest<HTMLElement>('[data-menu]')?.dataset.menu;
        if (!m) return;
        if (m === 'log') {
          const r = await api<{ list: EditLogEntry[] }>(`/api/topics/${t.id}/editlog`).catch(() => ({ list: [] }));
          openModal(editLogBox(r.list), () => {});
          return;
        }
        closeModal();
        if (needLogin()) return;
        if (m === 'sens') {
          if (await ask('センシティブとして申告しますか？', '3人集まると、書き込みが伏せられます。', '申告する')
            && await attempt(async () => setTopic((await api(`/api/topics/${t.id}/sensitive-report`, {})).topic))) toast('申告しました');
          return;
        }
        if (m.startsWith('sens-set-')) {
          await attempt(async () => setTopic((await api(`/api/admin/topics/${t.id}/sensitivity`, { level: Number(m.slice(9)) })).topic));
          return;
        }
        if (m === 'tags') openTags(); else if (m === 'edit') openTopicEdit();
        else if (m === 'report') {
          const t = topic()!;
          openModal(topicReportBox(t), async (ev) => {
            if (!(ev.target as Element).closest('[data-ok]')) return;
            const checked = $modal.querySelector<HTMLInputElement>('input[name="report-reason"]:checked');
            if (!checked) { shake($modal.querySelector('.report-reasons')); toast('通報の理由を選んでください'); return; }
            if (await attempt(async () => { await api(`/api/topics/${t.id}/report`, { reason: checked.value }); })) { closeModal(); toast('通報しました'); navigate(location.pathname, false); }
          });
        }
        else if (m === 'delete') {
          const t = topic()!;
          if (await ask('この話題を削除しますか？', '削除すると元に戻せません。', '削除する', true)
            && await attempt(async () => { await api(`/api/topics/${t.id}/delete`, {}); })) { toast('話題を削除しました'); navigate('/'); }
        }
      });
      return;
    }
    if (d.editLog !== undefined) {
      const r = await api<{ list: EditLogEntry[] }>(`/api/topics/${t.id}/editlog`).catch(() => ({ list: [] }));
      openModal(editLogBox(r.list), () => {});
      return;
    }
    if (d.stance) {
      // 書きかけの文が消えないよう、描き直さずに選択だけ切り替える
      ui.stance = d.stance;
      document.querySelectorAll<HTMLElement>('.stance-chip').forEach(x => x.classList.toggle('on', x.dataset.stance === d.stance));
      return;
    }

    if (d.post !== undefined) { await submitCompose(b.closest('.compose')!); return; }
    if (d.compose !== undefined) { openComposeSheet(); return; }
    // ここから先はログインが必要
    if (needLogin()) return;
    if (d.like) {
      const p = post(d.like)!;
      if (!t.myCamp && p.campId !== NONE) {
        const camp = t.camps.find(c => c.id === p.campId)!;
        if (!await ask(`「${camp.name}」の立場で参加しますか？`,
          '別の立場で書き込めば、あとから変わります。',
          '参加して「それな」')) return;
      }
      pop = `[data-like="${d.like}"]`;
      await attempt(async () => setTopic((await api(`/api/posts/${d.like}/like`, {})).topic));
      return;
    }
    if (d.naruhodoPost) {
      pop = `[data-naruhodo-post="${d.naruhodoPost}"]`;
      await attempt(async () => setTopic((await api(`/api/posts/${d.naruhodoPost}/naruhodo`, {})).topic));
      return;
    }
    if (d.reply) { openReply(post(d.reply)!); return; }
    if (d.chain) { openChain(post(d.chain)!); return; }
    if (d.debateOpen !== undefined) { openDebateOpen(); return; }
    if (d.report) { openReport(post(d.report)!); return; }
    if (d.mine) {
      const p = post(d.mine)!;
      if (!await ask('この「ひとこと」を削除しますか？', `『${cut(p.text, 40)}』<br>反論が付いていると「削除されました」と残ります。`, '削除する', true)) return;
      if (await attempt(async () => setTopic((await api(`/api/posts/${d.mine}/delete`, {})).topic))) toast('削除しました');
      return;
    }
    if (d.addPremise) {
      if (!await ask('この「補足」を「前提」に追加しますか？', '編集履歴にあなたの名前が記録されます。', '追加する')) return;
      await attempt(async () => setTopic((await api(`/api/topics/${t.id}/premises`, { fromPost: d.addPremise })).topic));
      return;
    }
    if (d.factAdd !== undefined || d.factEdit) { openFact(d.factEdit || null); return; }
    if (d.factDel) {
      const f = t.premise.find(x => x.id === d.factDel)!;
      if (!await ask('この前提を削除しますか？', `「${f.text}」<br>編集履歴にあなたの名前が記録されます。`, '削除する', true)) return;
      await attempt(async () => setTopic((await api(`/api/premises/${f.id}/delete`, {})).topic));
      return;
    }
    if (d.object) { await attempt(async () => setTopic((await api(`/api/premises/${d.object}/object`, {})).topic)); return; }
    if (d.tagsEdit !== undefined) { openTags(); return; }
    if (d.topicEdit !== undefined) { openTopicEdit(); return; }
    if (d.support) {
      const before = t.camps.length;
      if (await attempt(async () => setTopic((await api(`/api/proposals/${d.support}/support`, {})).topic)) && topic()!.camps.length > before) {
        toast('新しい立場が加わりました');
      }
      return;
    }
    if (d.propose !== undefined) { openProposal(); return; }
  };
}

// ログインしていない人の書きかけ：登録へ進む前に覚えておき、登録のあとで話題を開いたら欄に戻す（この端末だけ、1日まで）
const GUEST_DRAFT = 'gironba-guest-draft';
function saveGuestDraft(topicId: string, text: string, stance: string) {
  try { localStorage.setItem(GUEST_DRAFT, JSON.stringify({ topicId, text, stance, at: Date.now() })); } catch { /* 覚えられなくても、登録には進める */ }
}
function restoreGuestDraft() {
  const t = topic();
  if (!t || !page.viewer.loggedIn) return;
  let g: { topicId: string; text: string; stance: string; at: number } | null = null;
  try { g = JSON.parse(localStorage.getItem(GUEST_DRAFT) || 'null'); } catch { g = null; }
  if (!g || g.topicId !== t.id) return;
  try { localStorage.removeItem(GUEST_DRAFT); } catch { /* 消せなくても次で上書きされる */ }
  if (Date.now() - g.at > 24 * 60 * 60 * 1000) return;
  ui.stance = g.stance;
  const inline = document.querySelector<HTMLElement>('.compose-inline');
  if (inline && inline.offsetParent) {
    inline.classList.add('open');
    const ta = inline.querySelector('textarea')!;
    ta.value = g.text;
    document.querySelectorAll<HTMLElement>('.stance-chip').forEach(x => x.classList.toggle('on', x.dataset.stance === ui.stance));
    ta.focus();
  } else {
    openComposeSheet();
    const ta = $modal.querySelector('textarea');
    if (ta) ta.value = g.text;
  }
  toast('書きかけの「ひとこと」を戻しました。立場と内容を確かめて投稿してください');
}

async function submitCompose(box: Element): Promise<boolean> {
  const t = topic()!;
  const ta = box.querySelector('textarea')!;
  const text = ta.value.trim();
  if (!text) { shake(ta); return false; }
  if (!ui.stance) { shake(box.querySelector('.stance-chips')); toast('立場を選んでください'); return false; }
  if (!page.viewer.loggedIn && !page.viewer.guestOK) {
    saveGuestDraft(t.id, text, ui.stance);
    closeModal();
    navigate('/signup?next=' + encodeURIComponent(location.pathname));
    return false;
  }
  const btn = box.querySelector<HTMLButtonElement>('[data-post]');
  if (btn) btn.disabled = true;
  const ok = await attempt(async () => {
    const sources = readSources(box);
    if (!sources) return;
    const r = await api(`/api/topics/${t.id}/posts`, { text, stance: ui.stance, sources });
    // 送ったひとことが見えるように、絞り込みを外す
    const [campId] = ui.stance.split(':');
    if (ui.filter !== 'all' && ui.filter !== campId) ui.filter = 'all';
    setTopic(r.topic);
  });
  if (btn) btn.disabled = false;
  if (ok) toast('「ひとこと」を投稿しました');
  return ok;
}

// スマホ：右下の「書く」から、下から投稿欄がせり上がる
function openComposeSheet() {
  const t = topic()!;
  openModal(`<div class="box"><div class="sheet-head"><button class="ghost small" data-close>閉じる</button></div>${composer(t, page.viewer, ui)}</div>`, async (e) => {
    const target = e.target as Element;
    const chip = target.closest<HTMLElement>('[data-stance]');
    if (chip) {
      ui.stance = chip.dataset.stance!;
      $modal.querySelectorAll<HTMLElement>('.stance-chip').forEach(x => x.classList.toggle('on', x.dataset.stance === ui.stance));
      return;
    }
    if (target.closest('[data-propose]')) { if (!needLogin()) openProposal(); return; }
    if (target.closest('[data-post]') && await submitCompose($modal.querySelector('.compose')!)) { closeModal(); window.scrollTo(0, 0); }
  }, true);
}

function openReply(p: Post) {
  const t = topic()!;
  openModal(replyBox(t, p, page.viewer), async (e) => {
    if (!(e.target as Element).closest('[data-ok]')) return;
    const ta = document.getElementById('reply-text') as HTMLTextAreaElement;
    const sel = document.getElementById('reply-stance') as HTMLSelectElement | null;
    const isAnswer = p.campId === NONE;
    if (!ta.value.trim()) { shake(ta); return; }
    if (!isAnswer && !sel!.value) { shake(sel); return; }
    const sources = readSources($modal);
    if (!sources) return;
    if (await attempt(async () => setTopic((await api(`/api/topics/${t.id}/posts`, { text: ta.value, stance: isAnswer ? `${NONE}:a` : sel!.value, replyTo: p.id, sources })).topic))) {
      closeModal(); toast(isAnswer ? '回答を投稿しました' : '「反論」を投稿しました');
    }
  });
}

function openFact(id: string | null) {
  const t = topic()!;
  const f = id ? t.premise.find(x => x.id === id)! : null;
  openModal(factBox(f ? f.text : null, f ? f.sources : []), async (e) => {
    if (!(e.target as Element).closest('[data-ok]')) return;
    const ta = document.getElementById('fact-text') as HTMLTextAreaElement;
    if (!ta.value.trim()) { shake(ta); return; }
    const sources = readSources($modal);
    if (!sources) return;
    const ok = await attempt(async () => setTopic((await api(f ? `/api/premises/${f.id}/edit` : `/api/topics/${t.id}/premises`, { text: ta.value, sources })).topic));
    if (ok) closeModal();
  });
}

function openTags() {
  const t = topic()!;
  let chosen = [...t.tags];
  const all = sortTags([...new Set([...PRESET_TAGS.map(([x]) => x), ...chosen])]);
  openModal(tagsBox(all, chosen), async (e) => {
    const pick = (e.target as Element).closest<HTMLElement>('[data-tagpick]');
    if (pick) {
      const x = pick.dataset.tagpick!;
      if (chosen.includes(x)) chosen = chosen.filter(y => y !== x);
      else if (chosen.length >= 5) { toast('タグは5つまで選べます'); return; }
      else chosen.push(x);
      pick.classList.toggle('on', chosen.includes(x));
      return;
    }
    if (!(e.target as Element).closest('[data-ok]')) return;
    const extra = parseTags((document.getElementById('tag-extra') as HTMLInputElement).value);
    const next = [...new Set([...chosen, ...extra])].slice(0, 5);
    if (await attempt(async () => setTopic((await api(`/api/topics/${t.id}/tags`, { tags: next })).topic))) closeModal();
  });
}

function openTopicEdit() {
  const t = topic()!;
  openModal(topicEditBox(t), async (e) => {
    if (!(e.target as Element).closest('[data-ok]')) return;
    const title = document.getElementById('te-title') as HTMLInputElement;
    if (!title.value.trim()) { shake(title); return; }
    const camps: Record<string, string> = {};
    for (const i of $modal.querySelectorAll<HTMLInputElement>('[data-camp-name]')) {
      if (!i.value.trim()) { shake(i); return; }
      camps[i.dataset.campName!] = i.value;
    }
    if (await attempt(async () => setTopic((await api(`/api/topics/${t.id}/edit`, { title: title.value, camps })).topic))) {
      closeModal(); document.title = `${topic()!.title}｜GIRONBA`;
    }
  });
}

function openProposal() {
  const t = topic()!;
  openModal(proposalBox(t), async (e) => {
    if (!(e.target as Element).closest('[data-ok]')) return;
    const name = document.getElementById('pr-name') as HTMLInputElement;
    if (!name.value.trim()) { shake(name); return; }
    const ok = await attempt(async () => setTopic((await api(`/api/topics/${t.id}/proposals`, {
      name: name.value,
      reason: (document.getElementById('pr-reason') as HTMLInputElement).value,
      pole: (document.getElementById('pr-pole') as HTMLSelectElement).value,
    })).topic));
    if (ok) closeModal();
  });
}

// 話題の共有。どれも話題のアドレスだけを流す（匿名のひとことと X のアカウントは結びつかない）
async function copyText(text: string, done: string) {
  try { await navigator.clipboard.writeText(text); toast(done); }
  catch { window.prompt('コピーしてください', text); }
}
function openShare(t: TopicView) {
  const url = `${location.origin}/t/${t.id}`;
  openModal(shareBox(t), async (e) => {
    const target = e.target as Element;
    if (target.closest('[data-share-x]')) { openXIntent(`「${t.title}」\n立場ごとの意見をまとめています`, url); closeModal(); return; }
    if (target.closest('[data-copy-reply]')) { await copyText(`この件、GIRONBA で立場ごとに意見をまとめています\n${url}\n#GIRONBA`, '返信用の文をコピーしました'); closeModal(); return; }
    if (target.closest('[data-copy-link]')) { await copyText(url, 'リンクをコピーしました'); closeModal(); }
  });
}

// 通報：理由を選んで送る。送ったあとは話題を読み直す
function openReport(p: Post) {
  openModal(reportBox(p), async (e) => {
    if (!(e.target as Element).closest('[data-ok]')) return;
    const checked = $modal.querySelector<HTMLInputElement>('input[name="report-reason"]:checked');
    if (!checked) { shake($modal.querySelector('.report-reasons')); toast('通報の理由を選んでください'); return; }
    const ok = await attempt(async () => {
      await api(`/api/posts/${p.id}/report`, { reason: checked.value });
      const r = await fetch(location.pathname, { headers: { 'X-Gironba-Data': '1' } });
      const next = await r.json() as Page;
      if (next.data.kind === 'topic') setTopic(next.data.topic);
    });
    if (ok) { closeModal(); toast('通報しました'); }
  });
}

// ---------- 討論場 ----------
function openDebateOpen() {
  const t = topic()!;
  openModal(debateOpenBox(t), async (e) => {
    if (!(e.target as Element).closest('[data-ok]')) return;
    const campA = (document.getElementById('db-a') as HTMLSelectElement).value, campB = (document.getElementById('db-b') as HTMLSelectElement).value;
    if (campA === campB) { shake(document.getElementById('db-b')); toast('違う立場を2つ選んでください'); return; }
    const capacity = Number((document.getElementById('db-cap') as HTMLSelectElement).value);
    await attempt(async () => {
      const r = await api<{ ok: true; id: string }>(`/api/topics/${t.id}/debates`, { campA, campB, capacity });
      closeModal(); toast('討論の募集を始めました');
      navigate(`/t/${t.id}/d/${r.id}`);
    });
  });
}

function debate(): DebateView | null { return page.data.kind === 'debate' ? page.data.debate : null; }
// 描き直しても、書きかけの文は消さない（ほかのメンバーが保存したときは、保存された下書きに置き換える）
const DEBATE_DRAFT_SEL = ['#debate-draft', '.draft-q', '.draft-a', '.room-input textarea', '.comment-input textarea'];
function setDebate(x: DebateView | undefined, keepDrafts = true) {
  const before = debate();
  const keepDraft = keepDrafts && (!x || !before?.draft || !x.draft || x.draft.version === before.draft.version);
  const saved = DEBATE_DRAFT_SEL.map(sel => [...document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(sel)].map(el => el.value));
  if (x && page.data.kind === 'debate') page.data.debate = x;
  $app.innerHTML = renderPage(page, ui);
  DEBATE_DRAFT_SEL.forEach((sel, i) => {
    if (i < 3 && !keepDraft) return;          // 下書き（本文・質問・回答）は、版が変わったら新しいものを見せる
    if (i >= 3 && !keepDrafts) return;
    document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(sel).forEach((el, k) => { if (saved[i]?.[k] !== undefined) el.value = saved[i][k]; });
  });
  afterRender();
}
function draftForm() {
  return {
    text: (document.getElementById('debate-draft') as HTMLTextAreaElement | null)?.value ?? '',
    questions: [...document.querySelectorAll<HTMLInputElement>('.draft-q')].map(x => x.value.trim()).filter(Boolean),
    answers: [...document.querySelectorAll<HTMLTextAreaElement>('.draft-a')].map(x => x.value.trim()),
  };
}
function draftChanged(): boolean {
  const dr = debate()?.draft;
  if (!dr || !document.getElementById('debate-draft')) return false;
  const f = draftForm();
  return f.text !== dr.text || JSON.stringify(f.questions) !== JSON.stringify(dr.questions)
    || JSON.stringify(f.answers) !== JSON.stringify(dr.answers.slice(0, f.answers.length));
}

function bindDebate() {
  // 字数の表示
  const ta = document.getElementById('debate-draft') as HTMLTextAreaElement | null;
  if (ta) ta.oninput = () => {
    const n = [...ta.value].length, max = Number(ta.dataset.max);
    const c = document.getElementById('draft-count')!;
    c.textContent = String(n); c.parentElement!.classList.toggle('over', n > max);
  };
  $app.onclick = async (e) => {
    const x = debate()!;
    const b = (e.target as Element).closest<HTMLButtonElement>('button');
    if (!b || b.disabled) return;
    const d = b.dataset;
    if (needLogin()) return;
    const call = (name: string, body: unknown = {}, keep = true) =>
      attempt(async () => setDebate((await api<{ ok: true; debate: DebateView }>(`/api/debates/${x.id}/${name}`, body)).debate, keep));
    // 保存に失敗したとき（ほかの人が先に保存した）は、最新を読み込む
    const reload = async () => {
      const r = await fetch(location.pathname, { headers: { 'X-Gironba-Data': '1' } });
      const n = await r.json() as Page;
      if (n.data.kind === 'debate') setDebate(n.data.debate, false);
    };
    const saveDraft = async (): Promise<boolean> => {
      const f = draftForm();
      const ok = await attempt(async () => setDebate((await api<{ ok: true; debate: DebateView }>(`/api/debates/${x.id}/draft`, { ...f, baseVersion: x.draft?.version ?? 0 })).debate, false));
      if (!ok) await reload();
      return ok;
    };

    if (d.debateJoin) {
      if (!await ask(`「${x.camps[d.debateJoin as 'A' | 'B'].name}」チームで参加しますか？`, '討論の参加者は名前が表示されます。募集中であれば、参加を取り消せます。', '参加する')) return;
      if (await call('join', { side: d.debateJoin }) && debate()?.status === 'live') toast('チームがそろい、討論が始まりました');
      return;
    }
    if (d.debateAct === 'leave') { if (await ask('参加を取り消しますか？', '', '取り消す')) await call('leave'); return; }
    if (d.debateAct === 'watch') { if (await call('watch')) toast(debate()!.watching ? '更新があると「お知らせ」に通知されます' : '通知を解除しました'); return; }
    if (d.debateAct === 'draft') {
      if (!draftChanged()) { toast('変更はありません'); return; }
      if (await saveDraft()) toast('下書きを保存しました');
      return;
    }
    if (d.debateAct === 'approve') {
      // 書きかけがあれば先に保存する（保存すると「これで出す」はリセットされる）
      if (draftChanged()) { if (!await saveDraft()) return; toast('下書きを保存しました'); }
      await call('approve', {}, false);
      return;
    }
    if (d.debateAct === 'submit') {
      if (draftChanged()) {
        if (await saveDraft()) toast('変更を保存しました。もう一度「提出に同意」を集めてください');
        return;
      }
      const kind = x.current?.kind ?? '';
      if (!await ask('チームの発言として提出しますか？', `${kind}を提出します。提出したあとは直せません。`, '提出する')) return;
      if (await call('submit', { version: x.draft?.version }, false)) toast('提出しました');
      return;
    }
    if (d.draftHistory !== undefined && x.draft) {
      const dr = x.draft;
      openModal(draftHistoryBox(dr), async (ev) => {
        const r = (ev.target as Element).closest<HTMLElement>('[data-restore]');
        if (!r) return;
        const v = dr.versions.find(y => y.version === Number(r.dataset.restore))!;
        const ok = await attempt(async () => setDebate((await api<{ ok: true; debate: DebateView }>(`/api/debates/${x.id}/draft`,
          { text: v.text, questions: v.questions, answers: v.answers, baseVersion: dr.version })).debate, false));
        if (ok) { closeModal(); toast(`${v.version}回目の保存内容に戻しました`); } else await reload();
      });
      return;
    }
    if (d.debateWait !== undefined) {
      openModal(debateWaitBox(x), async (ev) => {
        if (!(ev.target as Element).closest('[data-ok]')) return;
        const kind = $modal.querySelector<HTMLInputElement>('input[name="wait-kind"]:checked')!.value;
        const reason = (document.getElementById('wait-reason') as HTMLInputElement).value;
        let body: Record<string, unknown>;
        if (kind === 'hours') {
          const h = Number((document.getElementById('wait-hours') as HTMLSelectElement).value);
          if (!h) { toast('延長できる時間が残っていません'); return; }
          body = { hours: h, reason };
        } else {
          const v = (document.getElementById('wait-until') as HTMLInputElement).value;
          if (!v) { shake(document.getElementById('wait-until')); return; }
          body = { until: new Date(v).getTime(), reason };
        }
        if (await call('wait', body)) { closeModal(); toast('持ち時間を延長しました'); }
      });
      return;
    }
    if (d.debateAtodashi) {
      const sid = d.debateAtodashi;
      openModal(atodashiBox(), async (ev) => {
        if (!(ev.target as Element).closest('[data-ok]')) return;
        if (await call('atodashi', { statementId: sid, note: (document.getElementById('atodashi-note') as HTMLInputElement).value })) closeModal();
      });
      return;
    }
    if (d.debateNaruhodo) { pop = `[data-debate-naruhodo="${d.debateNaruhodo}"]`; await call('naruhodo', { statementId: d.debateNaruhodo }); return; }
    if (d.debatePoll) { const [which, choice] = d.debatePoll.split(':'); await call('poll', { which, choice }); return; }
    if (d.debateRoom !== undefined || d.debateComment !== undefined) {
      const box = b.closest('.seat-input')!;
      const t = box.querySelector('textarea')!;
      if (!t.value.trim()) { shake(t); return; }
      const textValue = t.value;
      t.value = '';
      if (!await call(d.debateRoom !== undefined ? 'room' : 'comment', { text: textValue })) t.value = textValue;
    }
  };
}

// 討論場を開いているあいだも、30秒ごとに確かめる（作戦室や下書きの更新も拾う）
setInterval(async () => {
  const x = debate();
  if (!x || document.hidden || !$modal.hidden) return;
  try {
    const r = await fetch(location.pathname, { headers: { 'X-Gironba-Data': '1' } });
    const next = await r.json() as Page;
    if (next.data.kind !== 'debate' || next.data.debate.id !== x.id) return;
    const n = next.data.debate;
    const sig = (y: DebateView) => [y.status, y.statements.length, y.comments.length, y.room.length, y.draft?.version, y.draft?.approvals, y.peekDrafts.map(p => p.updatedAt).join(),
      y.members.A.length + y.members.B.length, y.current?.deadline, y.watching].join('|');
    if (sig(n) === sig(x)) return;
    const draftMoved = !!(n.draft && x.draft && n.draft.version !== x.draft.version);
    const typing = draftChanged();
    page.viewer = next.viewer;
    // 自分が書きかけのときは、ほかの人の保存で消さない（知らせるだけ）
    setDebate(n, !draftMoved || typing ? true : false);
    if (draftMoved && typing) toast('ほかのメンバーが下書きを保存しました。保存する前に「履歴」で内容を確認してください');
    else if (n.statements.length > x.statements.length) toast('新しい発言が提出されました');
  } catch { /* 次の回にまた確かめる */ }
}, 30000);

// ---------- 判定・管理画面・問い合わせ ----------
function bindCase() {
  $app.onclick = async (e) => {
    const b = (e.target as Element).closest<HTMLButtonElement>('button');
    if (!b || b.disabled) return;
    const cs = page.data.kind === 'case' ? page.data.case : null;
    if (!cs || needLogin()) return;
    const d = b.dataset;
    const redraw = (x: CaseView) => { if (page.data.kind === 'case') page.data.case = x; $app.innerHTML = renderPage(page, ui); afterRender(); };
    if (d.caseVote !== undefined) {
      const v = d.caseVote === '1';
      if (!await ask(v ? '「違反している」と判定しますか？' : '「問題ない」と判定しますか？', '判定はあとから変えられません。', '判定する', v)) return;
      if (await attempt(async () => redraw((await api<{ ok: true; case: CaseView }>(`/api/cases/${cs.id}/vote`, { violation: v })).case))) toast('判定しました。ご協力ありがとうございます');
      return;
    }
    if (d.adminDecide !== undefined) {
      const v = d.adminDecide === '1';
      if (!await ask(v ? '運営として「違反」とします' : '運営として「問題なし」とします', v ? '書き込みは非表示になり、書いた人は一定期間書き込めなくなります。' : '畳まれていた書き込みは元に戻ります。', '確定する', v)) return;
      await attempt(async () => redraw((await api<{ ok: true; case: CaseView }>(`/api/admin/cases/${cs.id}/decide`, { violation: v })).case));
      return;
    }
    if (d.caseStatement !== undefined) {
      const ta = document.getElementById('case-statement') as HTMLTextAreaElement;
      if (!ta.value.trim()) { shake(ta); return; }
      if (!await ask('言い分を送りますか？', '送れるのは1回だけで、あとから直せません。', '送る')) return;
      if (await attempt(async () => redraw((await api<{ ok: true; case: CaseView }>(`/api/cases/${cs.id}/statement`, { text: ta.value })).case))) toast('言い分を送りました');
      return;
    }
    if (d.topicRemove || d.topicRestore) {
      const remove = !!d.topicRemove;
      if (!await ask(remove ? 'この話題の削除を確定しますか？' : 'この話題を元に戻しますか？', remove ? '話題は削除され、立てた人は書き込みが止められます。中で議論していた人には、お知らせで伝えます。' : 'トップや検索に、また表示されます。', remove ? '削除を確定' : '元に戻す', remove)) return;
      if (await attempt(async () => { await api(`/api/admin/topics/${remove ? d.topicRemove : d.topicRestore}/confirm`, { remove }); })) { toast(remove ? '削除しました' : '元に戻しました'); navigate(location.pathname, false); }
      return;
    }
    if (d.appeal) {
      if (!await ask('異議を申し立てますか？', '別の利用者が判定し直します。異議を申し立てられるのは1回だけです。', '申し立てる')) return;
      await attempt(async () => {
        const r = await api<{ ok: true; caseId: string }>(`/api/posts/${d.appeal}/appeal`, {});
        toast('異議を申し立てました');
        navigate(`/cases/${r.caseId}`);
      });
    }
  };
}

function bindAdmin() {
  const here = () => location.pathname + location.search;
  const form = $app.querySelector<HTMLFormElement>('[data-admin-search]');
  if (form) form.onsubmit = (e) => {
    e.preventDefault();
    const q = (form.elements.namedItem('q') as HTMLInputElement).value.trim();
    const href = `/admin?tab=users${q ? `&q=${encodeURIComponent(q)}` : ''}`;
    history.replaceState(history.state, '', href); navigate(href, false);
  };
  $app.onclick = async (e) => {
    const b = (e.target as Element).closest<HTMLButtonElement>('button');
    if (!b || b.disabled) return;
    const d = b.dataset;
    if (d.topicRemove || d.topicRestore) {
      const remove = !!d.topicRemove;
      if (!await ask(remove ? 'この話題の削除を確定しますか？' : 'この話題を元に戻しますか？', remove ? '話題は削除され、立てた人は書き込みが止められます。中で議論していた人には、お知らせで伝えます。' : 'トップや検索に、また表示されます。', remove ? '削除を確定' : '元に戻す', remove)) return;
      if (await attempt(async () => { await api(`/api/admin/topics/${remove ? d.topicRemove : d.topicRestore}/confirm`, { remove }); })) { toast(remove ? '削除しました' : '元に戻しました'); navigate(here(), false); }
      return;
    }
    if (d.contactReply) {
      const ta = $app.querySelector<HTMLTextAreaElement>(`[data-reply-text="${d.contactReply}"]`)!;
      if (!ta.value.trim()) { shake(ta); return; }
      if (!await ask('返信しますか？', 'ログインしている人には「お知らせ」に、そうでない人にはメールで届きます。', '返信する')) return;
      if (await attempt(async () => { await api(`/api/admin/contacts/${d.contactReply}/reply`, { text: ta.value }); })) { toast('返信しました'); navigate(here(), false); }
      return;
    }
    if (d.contactDone) {
      if (await attempt(async () => { await api(`/api/admin/contacts/${d.contactDone}/done`, {}); })) navigate(here(), false);
      return;
    }
    if (d.inviteNew !== undefined) {
      const uses = Number(($app.querySelector('#invite-uses') as HTMLSelectElement | null)?.value ?? 5);
      if (await attempt(async () => { const r = await api<{ ok: true; code: string }>('/api/admin/invites', { uses }); toast(`招待コード ${r.code} を発行しました`); })) navigate(here(), false);
      return;
    }
    if (d.inviteDisable) {
      if (!await ask('この招待コードを無効にしますか？', 'これ以上、このコードでは登録できなくなります。登録済みの人はそのまま使えます。', '無効にする', true)) return;
      if (await attempt(async () => { await api(`/api/admin/invites/${d.inviteDisable}/disable`, {}); })) { toast('無効にしました'); navigate(here(), false); }
      return;
    }
    if (d.copy) {
      navigator.clipboard.writeText(d.copy).then(() => toast('コピーしました'), () => toast(d.copy!));
      return;
    }
    if (d.suspend) {
      const label = d.days === '1' ? '24時間' : `${d.days}日間`;
      if (!await ask(`${label}、書き込みを停止しますか？`, '本人の「お知らせ」に通知されます。違反の回数には数えません。', '停止する', true)) return;
      if (await attempt(async () => { await api(`/api/admin/users/${d.suspend}/suspend`, { days: d.days }); })) { toast('書き込みを停止しました'); navigate(here(), false); }
      return;
    }
    if (d.unsuspend) {
      if (!await ask('書き込みの停止を解除しますか？', '本人の「お知らせ」に通知されます。', '解除する')) return;
      if (await attempt(async () => { await api(`/api/admin/users/${d.unsuspend}/unsuspend`, {}); })) { toast('停止を解除しました'); navigate(here(), false); }
    }
  };
}

function bindContact() {
  const cat = document.getElementById('ct-category') as HTMLSelectElement | null;
  if (cat) cat.onchange = () => { document.getElementById('ct-removal-hint')!.hidden = cat.value !== 'removal'; };
  $app.onclick = async (e) => {
    const b = (e.target as Element).closest<HTMLButtonElement>('[data-contact-send]');
    if (!b || b.disabled) return;
    const $ = (id: string) => document.getElementById(id) as HTMLInputElement | null;
    const category = $('ct-category')!.value, body = ($('ct-body') as unknown as HTMLTextAreaElement).value;
    if (!category) { shake($('ct-category')); toast('種類を選んでください'); return; }
    if (!body.trim()) { shake($('ct-body')); return; }
    b.disabled = true;
    const ok = await attempt(async () => {
      await api('/api/contact', { category, body, targetUrl: $('ct-target')!.value, email: $('ct-email')?.value || '', turnstile: turnstileToken() });
    });
    if (ok) {
      document.getElementById('contact-form')!.hidden = true;
      document.getElementById('contact-result')!.innerHTML = `<div class="auth-sent"><b>送信しました</b><br>${page.viewer.loggedIn ? '返信は「お知らせ」に届きます。' : '返信先を書いていただいた場合は、そちらにお返事します。'}</div>`;
    } else resetTurnstile();
  };
}

// タイムラインで「返す」。往復が続くと議論場へ移る
function openChain(last: Post) {
  const t = topic()!;
  openModal(chainBox(t, last), async (e) => {
    if (!(e.target as Element).closest('[data-ok]')) return;
    const ta = document.getElementById('reply-text') as HTMLTextAreaElement;
    if (!ta.value.trim()) { shake(ta); return; }
    const sources = readSources($modal);
    if (!sources) return;
    await attempt(async () => {
      const r = await api<{ ok: true; arenaId: string | null; topic: TopicView }>(`/api/posts/${last.id}/chain`, { text: ta.value, sources });
      closeModal();
      if (r.arenaId) { toast('やり取りが続いたので、議論場に移りました'); navigate(`/t/${t.id}/a/${r.arenaId}`); return; }
      setTopic(r.topic); toast('返信しました');
    });
  });
}

// ---------- 議論場 ----------
function arena(): ArenaView | null { return page.data.kind === 'arena' ? page.data.arena : null; }
// 描き直しても、書きかけの文は消さない
const DRAFT_SEL = ['.arena-input textarea', '.arena-input .source-input', '.seat-input textarea'];
function setArena(a: ArenaView | undefined, keepDrafts = true) {
  const saved = keepDrafts ? DRAFT_SEL.map(sel => [...document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(sel)].map(x => x.value)) : [];
  const sourceOpen = !!document.querySelector('.arena-input .source-box:not([hidden])');
  if (a && page.data.kind === 'arena') page.data.arena = a;
  $app.innerHTML = renderPage(page, ui);
  if (keepDrafts) {
    DRAFT_SEL.forEach((sel, i) => document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(sel).forEach((x, k) => { x.value = saved[i]?.[k] ?? ''; }));
    if (sourceOpen) document.querySelector<HTMLElement>('.arena-input [data-source-toggle]')?.click();
  }
  afterRender();
}
// アテンションバウンス：自分の番が来たとき、その番につき一度だけ
let bouncedFor = '';
function bounceIfMyTurn() {
  const a = arena();
  if (!a || a.status !== 'open' || a.mySlot !== a.nextSlot) return;
  const last = a.msgs[a.msgs.length - 1]?.id || '';
  if (bouncedFor === last || reduceMotion()) return;
  bouncedFor = last;
  document.querySelector('.arena-status')?.classList.add('bounce');
}

// 引用：「相手の発言を引用」→ 相手の発言を選ぶ → その発言が小窓に大きく出る → 引用したい部分を指（マウス）でなぞる → 「この部分を引用」。
// スマホの文字選択は難しいので、なぞった文字の範囲を自前で数える。返信は交互なので、発言まるごとの引用はしない
const quoteSource = (text: string) => text.split('\n').filter(l => !/^[>＞]/.test(l)).join('\n').trim();
function openQuotePicker() {
  const a = arena()!;
  const others = a.msgs.filter(m => !m.mine).slice().reverse();
  if (!others.length) { toast('引用できる相手の発言がありません'); return; }
  openModal(`
    <div class="box quote-box">
      <h3>引用する発言を選んでください</h3>
      <p class="muted small">選んだあとで、その発言の中から引用したい部分だけを選べます。</p>
      <div class="quote-list">${others.map(m => `
        <button class="quote-pick" data-quote-pick="${m.id}"><span>${esc(cut(quoteSource(m.text), 90))}</span><span class="muted small">${esc(m.anon)} · ${ago(m.createdAt)}</span></button>`).join('')}
      </div>
      <div class="row"><button class="ghost small" data-close>キャンセル</button></div>
    </div>`, (e) => {
    const pick = (e.target as Element).closest<HTMLElement>('[data-quote-pick]');
    if (pick) openQuoteTrace(pick.dataset.quotePick!);
  });
}
function openQuoteTrace(pid: string) {
  const msg = arena()!.msgs.find(m => m.id === pid);
  if (!msg) return;
  const chars = [...quoteSource(msg.text)];
  let from = -1, to = -1, dragging = false;
  openModal(`
    <div class="box quote-box">
      <h3>引用したい部分をなぞってください</h3>
      <p class="muted small">文字の上を指（パソコンはマウス）で押したまま動かすと、色が付きます。色が付いた部分が、返信の頭に引用されます。</p>
      <div class="quote-trace">${chars.map((ch, i) => ch === '\n' ? '<br>' : `<span data-i="${i}">${esc(ch)}</span>`).join('')}</div>
      <div class="quote-preview muted small">引用される部分：まだ選ばれていません</div>
      <div class="row">
        <button class="ghost small" data-quote-back>戻る</button>
        <button class="primary small" data-quote-ok disabled>この部分を引用</button>
      </div>
    </div>`, (e) => {
    const t = e.target as Element;
    if (t.closest('[data-quote-back]')) { openQuotePicker(); return; }
    if (t.closest('[data-quote-ok]') && from >= 0) {
      const [lo, hi] = from <= to ? [from, to] : [to, from];
      const picked = chars.slice(lo, hi + 1).join('');
      const ta = $app.querySelector<HTMLTextAreaElement>('.arena-input textarea');
      closeModal();
      if (!ta) return;
      const quote = picked.split('\n').map(l => l.trim()).filter(Boolean).map(l => `＞ ${l}`).join('\n');
      ta.value = `${quote}\n${ta.value}`;
      ta.focus();
      ta.setSelectionRange(ta.value.length, ta.value.length);
    }
  });
  const box = $modal.querySelector<HTMLElement>('.quote-trace')!;
  const preview = $modal.querySelector<HTMLElement>('.quote-preview')!;
  const ok = $modal.querySelector<HTMLButtonElement>('[data-quote-ok]')!;
  const paint = () => {
    const [lo, hi] = from <= to ? [from, to] : [to, from];
    box.querySelectorAll<HTMLElement>('span[data-i]').forEach(sp => { const i = Number(sp.dataset.i); sp.classList.toggle('on', from >= 0 && i >= lo && i <= hi); });
    const text = from >= 0 ? chars.slice(lo, hi + 1).join('').trim() : '';
    preview.textContent = `引用される部分：${text ? `「${cut(text, 60)}」` : 'まだ選ばれていません'}`;
    ok.disabled = !text;
  };
  // 指の下の文字。文字の間や余白に触れたときは、いちばん近い文字にする（指は文字より太いため）
  const spans = [...box.querySelectorAll<HTMLElement>('span[data-i]')];
  const indexAt = (x: number, y: number) => {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    if (el && el.dataset.i !== undefined && box.contains(el)) return Number(el.dataset.i);
    let best: number | null = null, bestD = Infinity;
    for (const sp of spans) {
      const r = sp.getBoundingClientRect();
      const dx = x < r.left ? r.left - x : x > r.right ? x - r.right : 0;
      const dy = y < r.top ? r.top - y : y > r.bottom ? y - r.bottom : 0;
      const d = dy * 4 + dx;   // 行がずれるより、同じ行の近い文字を選ぶ
      if (d < bestD) { bestD = d; best = Number(sp.dataset.i); }
    }
    return best;
  };
  box.addEventListener('pointerdown', (e) => {
    const i = indexAt(e.clientX, e.clientY);
    if (i === null) return;
    e.preventDefault();
    dragging = true; from = to = i; paint();
    box.setPointerCapture(e.pointerId);
  });
  box.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const i = indexAt(e.clientX, e.clientY);
    if (i !== null && i !== to) { to = i; paint(); }
  });
  const stop = () => { dragging = false; };
  box.addEventListener('pointerup', stop);
  box.addEventListener('pointercancel', stop);
}

function bindArena() {
  const av = arena();
  if (av?.topicSensitivity && !ui.revealed.has(av.topicId) && revealedList().includes(av.topicId)) { revealTopic(av.topicId); return; }
  $app.onclick = async (e) => {
    const a = arena()!;
    if ((e.target as Element).closest('[data-sens-reveal]')) { revealTopic(a.topicId); return; }
    const b = (e.target as Element).closest<HTMLButtonElement>('button');
    if (!b || b.disabled) return;
    const d = b.dataset;
    if (d.quoteStart !== undefined) { openQuotePicker(); return; }
    if (d.sourceToggle !== undefined || d.sourceClose !== undefined) return;
    if (d.shareRecruit) { await shareRecruit(a, d.shareRecruit as 'A' | 'B'); return; }   // ログインしていない人も広められる
    if (d.arenaView !== undefined || d.watchersSet !== undefined) return;   // 見え方の切り替えは、ログインしていなくても使える（処理は別の所）
    if (needLogin()) return;
    const call = (name: string, body: unknown = {}) =>
      attempt(async () => setArena((await api<{ ok: true; arena: ArenaView }>(`/api/arenas/${a.id}/${name}`, body)).arena));

    if (d.arenaAct === 'post') {
      const box = $app.querySelector('.arena-input')!;
      const ta = box.querySelector('textarea')!;
      if (!ta.value.trim()) { shake(ta); return; }
      const sources = readSources(box);
      if (!sources) return;
      b.disabled = true;
      const ok = await attempt(async () => setArena((await api<{ ok: true; arena: ArenaView }>(`/api/arenas/${a.id}/post`, { text: ta.value, sources })).arena, false));
      b.disabled = false;
      if (ok) toast('返信しました');
      return;
    }
    if (d.arenaAct === 'concede') {
      if (await ask('相手の意見に納得して終えますか？', 'ここで議論が終わります。', '納得して終える')) await call('concede');
      return;
    }
    if (d.arenaNote !== undefined) {
      const presets = ['離席中', '考え中', '夜に返信します', '明日返信します'];
      const cur = a.players[a.mySlot!].note ?? '';
      openModal(`
        <div class="box">
          <h3>状態を出す</h3>
          <p class="muted small">相手と観客に表示されます。あなたが返信すると消えます。</p>
          <div class="note-presets">${presets.map(p => `<button class="small ${p === cur ? 'on' : ''}" data-note="${p}">${p}</button>`).join('')}</div>
          <input type="text" id="note-free" maxlength="20" placeholder="自由に書く（20文字まで）" value="${presets.includes(cur) ? '' : esc(cur)}">
          <div class="row">
            ${cur ? '<button class="ghost small" data-note="">消す</button>' : '<button class="ghost small" data-close>キャンセル</button>'}
            <button class="primary small" data-note-free>出す</button>
          </div>
        </div>`, async (ev) => {
        const t = ev.target as Element;
        const preset = t.closest<HTMLElement>('[data-note]');
        const text = preset ? preset.dataset.note! : t.closest('[data-note-free]') ? ($modal.querySelector('#note-free') as HTMLInputElement).value : null;
        if (text === null) return;
        if (await call('note', { text })) closeModal();
      });
      return;
    }
    if (d.arenaAct === 'wait-open') {
      openModal(waitAskBox(a.firstWaitFree), async (ev) => {
        if (!(ev.target as Element).closest('[data-ok]')) return;
        const hours = Number(($modal.querySelector('#wait-hours') as HTMLSelectElement).value);
        const reason = ($modal.querySelector('#wait-reason') as HTMLInputElement).value;
        if (await call('ask-wait', { hours, reason })) { closeModal(); toast(a.firstWaitFree ? '持ち時間を延長しました' : '延長を申請しました。相手の返事を待っています'); }
      });
      return;
    }
    if (d.waitAnswer) {
      const yes = d.yes === '1';
      if (!yes && !await ask('延長を承認しませんか？', '期限までの観客の投票で賛成が多ければ、延長されます。', '承認しない')) return;
      if (await call('answer-wait', { waitId: d.waitAnswer, yes })) toast(yes ? '持ち時間を延長しました' : '観客の投票に任せました');
      return;
    }
    if (d.waitVote) { await call('vote-wait', { waitId: d.waitVote, yes: d.yes === '1' }); return; }
    if (d.arenaAct === 'recruit') {
      if (await ask('交代を募集しますか？', '同じ立場の人が引き継ぐと、あなたは議論から抜けます。', '募集する') && await call('recruit')) toast('観客席に交代の募集を出しました');
      return;
    }
    if (d.arenaAct === 'deadlock') {
      const mine = a.mySlot!, other = mine === 'A' ? 'B' : 'A';
      if (a.deadlock[mine]) { await call('deadlock'); return; }   // 自分の提案を取り消すだけなので、確認しない
      const ok = a.deadlock[other]
        ? await ask('平行線で終えることに同意しますか？', '同意すると、ここで議論が終わります。', '同意する')
        : await ask('平行線として終えることを提案しますか？', '相手も同意すると、議論が終わります。', '提案する');
      if (ok) await call('deadlock');
      return;
    }
    if (d.arenaAct === 'end') {
      if (await ask('この議論を終了しますか？', '相手の時間切れにより終了します。勝ち負けは付きません。', '終了する')) await call('end');
      return;
    }
    if (d.arenaAct === 'watch') {
      if (await call('watch')) toast(arena()!.watching ? '更新があると「お知らせ」に通知されます' : '通知を解除しました');
      return;
    }
    if (d.arenaAct) { await call(d.arenaAct); return; }   // 平行線・待つ
    if (d.addendum !== undefined) {
      const last = a.msgs[a.msgs.length - 1];
      openModal(addendumBox(last.text), async (ev) => {
        if (!(ev.target as Element).closest('[data-ok]')) return;
        const ta = document.getElementById('add-text') as HTMLTextAreaElement;
        if (!ta.value.trim()) { shake(ta); return; }
        if (await call('addendum', { text: ta.value })) closeModal();
      });
      return;
    }
    if (d.naruhodo) { pop = `[data-naruhodo="${d.naruhodo}"]`; await call('naruhodo', { postId: d.naruhodo }); return; }
    if (d.take) {
      if (await ask('交代して参加しますか？', 'この席で議論を引き継ぎます。持ち時間は今から24時間です。', '交代して参加する') && await call('take', { slot: d.take })) toast('交代しました');
      return;
    }
    if (d.volunteer) { if (await call('volunteer', { slot: d.volunteer })) toast('立候補しました。相手が承認すると交代します'); return; }
    if (d.accept) {
      if (await ask(`${esc(d.accept)} と交代しますか？`, `あなたは観客になります。`, '交代する') && await call('accept', { anon: d.accept })) toast('交代しました');
      return;
    }
    if (d.comment !== undefined) {
      const ta = b.closest('.seat-input')!.querySelector('textarea')!;
      if (!ta.value.trim()) { shake(ta); return; }
      const text = ta.value;
      ta.value = '';
      if (!await call('comment', { text })) ta.value = text;
    }
  };
}

// 交代の募集を X で広める。文には話題と立場だけを入れ、匿名 ID や募集した人は入れない
async function shareRecruit(a: ArenaView, slot: 'A' | 'B') {
  if (a.mySlot && !await ask('X に投稿しますか？',
    'あなたの X アカウントと、この議論場の ID が結びつくかもしれません。', 'X を開く')) return;
  const camp = a.camps.find(c => c.id === a.players[slot].campId)?.name ?? '';
  const text = `「${a.topicTitle}」の議論場で、『${camp}』側の交代を募集しています。続きを引き継いでくれる人はいませんか`;
  openXIntent(text, `${location.origin}/t/${a.topicId}/a/${a.id}`);
}
// X の「ポストの下書き」を開く（投稿するかどうかは本人が決める。GIRONBA からは投稿しない）
function openXIntent(text: string, url: string) {
  const q = new URLSearchParams({ text, url, hashtags: 'GIRONBA' });
  window.open(`https://x.com/intent/post?${q}`, '_blank', 'noopener');
}

// 議論場を開いているあいだは、30秒ごとに「変わったか」だけを軽く確かめ、変わっていたときだけ全部を読み直す
setInterval(async () => {
  const a = arena();
  if (!a || document.hidden || !$modal.hidden) return;
  try {
    const v = await fetch(`/api/arenas/${a.id}/ver`).then(r => r.json() as Promise<{ ver: string | null }>);
    if (!v.ver || v.ver === a.ver || arena()?.id !== a.id) return;
    const r = await fetch(location.pathname, { headers: { 'X-Gironba-Data': '1' } });
    const next = (await r.json() as Page);
    if (next.data.kind !== 'arena' || arena()?.id !== next.data.arena.id) return;
    const n = next.data.arena;
    const newTurn = n.msgs.length > a.msgs.length && !n.msgs[n.msgs.length - 1].mine;
    page.viewer = next.viewer;
    setArena(n);
    if (newTurn) toast(n.mySlot === n.nextSlot ? '相手が返信しました。あなたの番です' : '新しい書き込みがあります');
  } catch { /* 通信できなかったときは、次の回にまた確かめる */ }
}, 30000);

// ---------- 話題を立てる ----------
let similarTimer = 0;
function bindNew() {
  const $ = <T extends HTMLElement = HTMLInputElement>(id: string) => document.getElementById(id) as T | null;
  if (!$('nt-title')) return;
  const chosenTags = () => [...new Set([...ui.newTags, ...parseTags($('nt-tags')!.value)])].slice(0, 5);
  const showSimilar = () => {
    clearTimeout(similarTimer);
    similarTimer = window.setTimeout(async () => {
      const title = $('nt-title')!.value.trim();
      if (!title) { $('nt-similar')!.innerHTML = ''; return; }
      const r = await api<{ list: { id: string; title: string }[] }>(`/api/similar?title=${encodeURIComponent(title)}&tags=${encodeURIComponent(chosenTags().join(' '))}`).catch(() => ({ list: [] }));
      $('nt-similar')!.innerHTML = similarBox(r.list);
    }, 300);
  };
  // 最初のひとことの立場ボタン。立場の欄に書いた名前（空欄なら「そう思う／そう思わない」）がそのまま出る
  const stanceNames = (): [string, string][] => [
    ['c1', $('nt-a')!.value.trim() || 'そう思う'],
    ['c2', $('nt-b')!.value.trim() || 'そう思わない'],
    ...($('nt-mid')!.value.trim() ? [['c3', $('nt-mid')!.value.trim()] as [string, string]] : []),
  ];
  const drawFirstStance = () => {
    if (ui.newFirst && !stanceNames().some(([id]) => id === ui.newFirst)) ui.newFirst = '';
    $('nt-first-stance')!.innerHTML = firstStanceChips(stanceNames(), ui.newFirst);
  };
  ['nt-a', 'nt-b', 'nt-mid'].forEach(id => { $(id)!.oninput = drawFirstStance; });
  drawFirstStance();
  $('nt-title')!.oninput = showSimilar;
  $('nt-tags')!.oninput = showSimilar;
  $app.onclick = async (e) => {
    const target = e.target as Element;
    const first = target.closest<HTMLElement>('[data-first]');
    if (first) { ui.newFirst = first.dataset.first!; drawFirstStance(); return; }
    const pick = target.closest<HTMLElement>('[data-tagpick]');
    if (pick) {
      const x = pick.dataset.tagpick!;
      if (ui.newTags.includes(x)) ui.newTags = ui.newTags.filter(y => y !== x);
      else if (chosenTags().length >= 5) { toast('タグは5つまで選べます'); return; }
      else ui.newTags.push(x);
      pick.classList.toggle('on', ui.newTags.includes(x));
      showSimilar();
      return;
    }
    const btn = target.closest<HTMLButtonElement>('[data-create]');
    if (!btn) return;
    const title = $('nt-title')!.value.trim(), a = $('nt-a')!.value.trim(), b = $('nt-b')!.value.trim();
    if (!title) { shake($('nt-title')); return; }
    if (a && !b) { shake($('nt-b')); return; }
    if (b && !a) { shake($('nt-a')); return; }
    const firstText = $<HTMLTextAreaElement>('nt-first')!.value.trim();
    if (firstText && !ui.newFirst) { shake($('nt-first-stance')); toast('「ひとこと」の立場を選んでください'); return; }
    const token = turnstileToken();
    if (!token) { toast('確認が完了するまで、少しお待ちください'); return; }
    btn.disabled = true;
    await attempt(async () => {
      const r = await api<{ ok: true; id: string }>('/api/topics', {
        title, a, b, mid: $('nt-mid')!.value.trim(),
        premise: $<HTMLTextAreaElement>('nt-premise')!.value.split('\n'),
        tags: chosenTags(), first: firstText, firstStance: ui.newFirst, origin: $('nt-origin')!.value.trim(), turnstile: token,
        sensitivity: Number($<HTMLSelectElement>('nt-sens')?.value || 0),
      });
      navigate(`/t/${r.id}`);
    });
    resetTurnstile();
  };
}

// ---------- ログインと登録 ----------
let sentEmail = '';
function bindLogin() {
  $app.onclick = async (e) => {
    const codeBtn = (e.target as Element).closest<HTMLButtonElement>('[data-code-login]');
    if (codeBtn) {
      const input = document.getElementById('login-code') as HTMLInputElement;
      if (!/^\d{6}$/.test(input.value.trim())) { shake(input); toast('6桁の確認コードを入力してください'); return; }
      codeBtn.disabled = true;
      await attempt(async () => {
        const next = page.data.kind === 'login' ? page.data.next : '/';
        const r = await api<{ ok: true; next: string }>('/api/auth/code', { email: sentEmail, code: input.value.trim(), next });
        location.href = r.next;   // ログインの状態が変わるので、ページごと読み直す
      });
      codeBtn.disabled = false;
      return;
    }
    const btn = (e.target as Element).closest<HTMLButtonElement>('[data-email-login]');
    if (!btn) return;
    const input = document.getElementById('login-email') as HTMLInputElement;
    if (!input.value.trim()) { shake(input); return; }
    const token = turnstileToken();
    if (!token) { toast('確認が完了するまで、少しお待ちください'); return; }
    btn.disabled = true;
    await attempt(async () => {
      const d = page.data.kind === 'login' ? page.data : null;
      const r = await api<{ ok: true; devLink?: string; devCode?: string }>('/api/auth/email', { email: input.value, turnstile: token, next: d?.next || '/', mode: d?.mode });
      sentEmail = input.value.trim();
      document.getElementById('login-result')!.innerHTML = `
        <div class="auth-sent">
          <b>メールを送信しました</b><br>
          ${esc(sentEmail)} 宛てのメールに記載されたリンクを開くか、確認コードを入力してください（有効期限は15分です）。
          ${r.devLink ? `<br><span class="muted">開発用の環境のため、メールの代わりにここに表示しています：<a href="${r.devLink}">リンク</a>・コード ${r.devCode}</span>` : ''}
        </div>
        <form class="code-form" onsubmit="return false">
          <label for="login-code">確認コード</label>
          <input type="text" id="login-code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="6桁の数字">
          <button class="primary" data-code-login>${d?.mode === 'signup' ? '次へ' : 'ログイン'}</button>
          <p class="auth-hint">メールが届かない場合は、迷惑メールフォルダもご確認ください。</p>
        </form>`;
      document.getElementById('login-code')?.focus();
      document.getElementById('email-form')!.hidden = true;
    });
    resetTurnstile();
  };
}

function bindWelcome() {
  $app.onclick = async (e) => {
    const btn = (e.target as Element).closest<HTMLButtonElement>('[data-signup]');
    if (!btn) return;
    const handle = document.getElementById('w-handle') as HTMLInputElement;
    const invite = document.getElementById('w-invite') as HTMLInputElement | null;
    const agree = document.getElementById('w-agree') as HTMLInputElement;
    if (!handle.value.trim()) { shake(handle); return; }
    if (!agree.checked) { shake(agree.closest('label')); toast('「参加する前に」と利用規約をご確認ください'); return; }
    const token = turnstileToken();
    if (!token) { toast('確認が完了するまで、少しお待ちください'); return; }
    btn.disabled = true;
    await attempt(async () => {
      const direct = !!document.querySelector('#welcome-form[data-direct]');
      const r = await api<{ ok: true; next: string }>(direct ? '/api/auth/signup-invite' : '/api/auth/signup', { handle: handle.value, invite: invite?.value || '', agree: true, turnstile: token });
      toast('登録しました');
      navigate(r.next);
    });
    resetTurnstile();
  };
}

// 画面の色。cookie に入れて、サーバーが最初から <html data-theme> を付けて返す（読み込み時のちらつきを防ぐ）
function markTheme() {
  const cur = document.documentElement.dataset.theme ?? '';
  document.querySelectorAll<HTMLButtonElement>('[data-theme-set]').forEach(b => b.classList.toggle('on', b.dataset.themeSet === cur));
}
function setTheme(t: string) {
  if (t) {
    document.documentElement.dataset.theme = t;
    document.cookie = `theme=${t}; path=/; max-age=${60 * 60 * 24 * 400}; SameSite=Lax; Secure`;
  } else {
    delete document.documentElement.dataset.theme;
    document.cookie = 'theme=; path=/; max-age=0; SameSite=Lax; Secure';
  }
  markTheme();
}

// ページ下・右の欄・マイページのどこから押しても切り替わる
document.addEventListener('click', (e) => {
  const th = (e.target as Element).closest<HTMLButtonElement>('[data-theme-set]');
  if (th) setTheme(th.dataset.themeSet ?? '');
  // 右上のボタン：自動 → ライト → ダーク → 自動
  if ((e.target as Element).closest('[data-theme-toggle]')) {
    const order = ['', 'light', 'dark'];
    const next = order[(order.indexOf(document.documentElement.dataset.theme ?? '') + 1) % order.length];
    setTheme(next);
    // 続けて押したときは、前のお知らせを消して1つだけにする
    document.querySelectorAll('.toast.theme-note').forEach(x => x.remove());
    toast(`画面の色：${next === 'light' ? 'ライト' : next === 'dark' ? 'ダーク' : '自動（端末の設定に合わせる）'}`).classList.add('theme-note');
  }
});

// メールアドレスの変更（登録）：新しいアドレスを入れる → 届いた確認コードを入れる
function openEmailChange(current: string | null) {
  openModal(`
    <div class="box">
      <h3>${current ? 'メールアドレスを変更' : 'メールアドレスを登録'}</h3>
      <p class="muted small">新しいアドレスに確認コードを送ります。${current ? '変更すると、前のアドレスにも変更したことをお知らせします。' : ''}</p>
      <input type="email" id="em-new" autocomplete="email" placeholder="you@example.com">
      <div class="row"><button class="ghost small" data-close>キャンセル</button><button class="primary small" data-em-send>確認コードを送る</button></div>
    </div>`, async (e) => {
    if (!(e.target as Element).closest('[data-em-send]')) return;
    const input = $modal.querySelector<HTMLInputElement>('#em-new')!;
    const email = input.value.trim();
    if (!email) { shake(input); return; }
    let dev = '';
    if (!await attempt(async () => { const r = await api<{ ok: true; devCode?: string }>('/api/me/email/start', { email }); dev = r.devCode ?? ''; })) return;
    openModal(`
      <div class="box">
        <h3>確認コードを入力</h3>
        <p class="muted small">${esc(email)} に届いた6桁の確認コードを入力してください。有効期限は15分です。${dev ? `<br>（開発用：コード ${dev}）` : ''}</p>
        <input type="text" id="em-code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="6桁の数字">
        <div class="row"><button class="ghost small" data-close>キャンセル</button><button class="primary small" data-em-ok>${current ? '変更する' : '登録する'}</button></div>
      </div>`, async (ev) => {
      if (!(ev.target as Element).closest('[data-em-ok]')) return;
      const ci = $modal.querySelector<HTMLInputElement>('#em-code')!;
      if (ci.value.replace(/\D/g, '').length !== 6) { shake(ci); return; }
      if (await attempt(async () => { await api('/api/me/email/confirm', { code: ci.value }); })) {
        closeModal(); toast(current ? 'メールアドレスを変更しました' : 'メールアドレスを登録しました'); navigate('/me', false);
      }
    });
  });
}

// テーマを当てる（html の data-skin と、書体の読み込み）
let trySkin: string | null = null;   // 試しているテーマ（保存しない）
function paintSkin(id: string) {
  if ((document.documentElement.dataset.skin ?? '') === id) return;
  if (id) document.documentElement.dataset.skin = id; else delete document.documentElement.dataset.skin;
  document.getElementById('skin-font')?.remove();
  const url = skinFontUrl(id);
  if (url) {
    const l = document.createElement('link');
    l.rel = 'stylesheet'; l.id = 'skin-font'; l.href = url;
    document.head.appendChild(l);
  }
}
function applySkin(id: string) {
  page.viewer.skin = id || null; trySkin = null;
  paintSkin(id);
}
// 試しているテーマは、テーマの一覧にいる間だけ。ほかのページでは使っているテーマに戻す
function markSkin() {
  if (page.data.kind !== 'trophies') trySkin = null;
  paintSkin(trySkin ?? page.viewer.skin ?? '');
  document.querySelectorAll<HTMLElement>('[data-skin-try]').forEach(el => el.classList.toggle('trying', trySkin !== null && el.dataset.skinTry === trySkin));
}
function bindTrophies() {
  $app.onclick = async (e) => {
    const target = e.target as Element;
    const buy = target.closest<HTMLButtonElement>('[data-skin-buy]');
    if (buy) {
      const id = buy.dataset.skinBuy!;
      if (!await ask(`「${buy.dataset.name}」を買いますか？`, `${buy.dataset.price} ポイントを使います。`, '買う')) return;
      if (await attempt(async () => { await api('/api/skins/buy', { id }); })) { applySkin(id); navigate('/me/trophies?tab=skin', false); }
      return;
    }
    const use = target.closest<HTMLButtonElement>('[data-skin-use]');
    if (use) {
      const id = use.dataset.skinUse!;
      if (await attempt(async () => { await api('/api/skins/use', { id }); })) { applySkin(id); navigate('/me/trophies?tab=skin', false); }
      return;
    }
    const row = target.closest<HTMLElement>('[data-skin-try]');
    if (row) { trySkin = row.dataset.skinTry!; markSkin(); }
  };
}

function bindMe() {
  $app.onclick = async (e) => {
    const target = e.target as Element;
    if (target.closest('[data-email-change]')) { openEmailChange(page.data.kind === 'me' ? page.data.email : null); return; }
    if (target.closest('[data-me-save]')) {
      const input = document.getElementById('me-handle') as HTMLInputElement;
      if (!input.value.trim()) { shake(input); return; }
      if (!await ask('名前を変更しますか？', `次に変えられるのは30日後です。`, '変更する')) return;
      if (await attempt(async () => { await api('/api/me/handle', { handle: input.value }); })) { toast('名前を変更しました'); navigate('/me', false); }
      return;
    }
    if (target.closest('[data-logout]')) {
      await api('/api/auth/logout', {}).catch(() => {});
      location.href = '/';
    }
  };
}

afterRender();
